/**
 * @fileoverview A component that provides a side menu for the dashboard.
 * It uses the `useLocalStorage` hook to get the user's permissions and display the appropriate menu items.
 *
 * @example
 * <SideMenu />
 */

/* eslint-disable react/jsx-no-target-blank */

import Logo from '../assets/logo.svg'
import LogoutIcon from '../assets/logout.svg'
import ExternalIcon from '../assets/menu/external.svg'
import HelpIcon from '../assets/menu/help.svg'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { logout } from '../utils/logout'
import { ParticipantsMenuItem } from './menu/ParticipantsMenuItem'
import { SitesMenuItem } from './menu/SitesMenuItem'
import { StaffMenuItem } from './menu/StaffMenuItem'
import { StudiesMenuItem } from './menu/StudiesMenuItem'

/**
 * @function hasPermission
 * @description Checks if the user has a given permission.
 * @param {string[]} permissions - The user's permissions.
 * @param {string} permission - The permission to check for.
 * @returns {boolean} - Whether the user has the permission.
 */
function hasPermission(permissions: string[] = [], permission: string): boolean {
    return permissions.includes(permission)
}

/**
 * @function SideMenu
 * @description Component for displaying the side menu.
 * @returns {JSX.Element | null} - The SideMenu component.
 */
export function SideMenu() {
    const [, get] = useLocalStorage()
    const user = get<Novoic.API.User>('user')
    const permissions = user?.permissions
    return (
        <section className="fixed inset-0 min-h-screen flex z-50 flex-col w-64 bg-white border-r shrink-0 px-4 pt-8 pb-4">
            <div className="flex mb-8 w-[224px] h-[40px] justify-center items-center">
                <Logo className="mr-3" />
                <span className="font-serif font-normal text-xl pr-1.5">Novoic</span>
                <span className="font-normal text-lg">Dashboard</span>
            </div>

            {/* <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white w-full mb-10 px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={() =>
                    open({
                        canClose: true,
                        content: <StatusVerificationModal />,
                    })
                }
            >
                <PlusIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">Create New Test</span>
            </button> */}

            {/* <div className="p-4 rounded-lg hover:bg-indigo-50 cursor-pointer mb-2">
                <div className="flex items-center">
                    <ChartBarSquareIcon className="w-[18px] mr-4" />
                    <span className="font-medium text-sm text-gray-600">Monitoring</span>
                </div>
            </div> */}

            {/* <TestsMenuItem /> */}

            <StudiesMenuItem />
            <ParticipantsMenuItem />
            {hasPermission(permissions, 'SITE.LIST') && <SitesMenuItem />}
            {hasPermission(permissions, 'USER.LIST') && <StaffMenuItem />}

            {/*
                <div className="p-4 rounded-lg hover:bg-indigo-50 cursor-pointer mb-2">
                    <div className="flex items-center cursor-pointer">
                        <UserCircleIcon className="w-[18px] mr-4" />
                        <span className="font-medium text-sm text-gray-600">Staff</span>
                    </div>
                </div>

                <div className="p-4 rounded-lg hover:bg-indigo-50 cursor-pointer mb-2">
                    <div className="flex items-center cursor-pointer">
                        <Cog8ToothIcon className="w-[18px] mr-4" />
                        <span className="font-medium text-sm text-gray-600">Settings</span>
                    </div>
                </div>
            */}

            <div className="flex grow items-end">
                <div className="w-full flex flex-col divide-y gap-y-4">
                    <a
                        href="https://help.novoic.com"
                        target="_blank"
                        className="flex gap-4 p-3 mt-4 items-center group help rounded-lg cursor-pointer group container transition-all hover:bg-indigo-100 hover:text-gray-900"
                    >
                        <HelpIcon className="group-[.help:hover]:fill-indigo-500" />
                        <span className="flex grow text-sm font-medium text-gray-600 group-[.help:hover]:text-gray-900">
                            Help Center
                        </span>
                        <ExternalIcon className="group-[.help:hover]:stroke-indigo-500 group-[.help:hover]:block hidden" />
                    </a>
                    <div>
                        <button
                            onClick={() => logout()}
                            className="flex gap-4 p-3 mt-4 items-center rounded-lg cursor-pointer group container transition-all hover:bg-indigo-100"
                        >
                            <LogoutIcon className="w-6 h-6 stroke-gray-400 stroke-2 transition-all group-[.container:hover]:stroke-indigo-500" />
                            <div className="flex flex-col">
                                <div className="flex flex-col items-start transition-all group-[.container:hover]:hidden">
                                    <span className="text-sm font-medium text-gray-600 text-ellipsis whitespace-nowrap max-w-[150px] overflow-hidden">
                                        {user?.name}
                                    </span>
                                </div>
                                <div className="transition-all hidden group-[.container:hover]:flex">
                                    <span className="text-sm font-medium text-gray-900">
                                        Logout
                                    </span>
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    )
}
